import { Account } from '@/domain/usecases'
import { LDClient, LDEvaluationDetail, LDFlagSet } from 'launchdarkly-react-client-sdk'

export namespace FeatureFlag {
  export class Key {
    private static allValues: { [name: string]: Key } = {}

    static readonly COMPANIES_ON_COMPANY_MANAGER_DASHBOARDS = new Key(
      'skillcustomer-companies-on-admin-dashboards',
      false
    )

    static readonly COMPANIES_ON_ENROLLMENT_ROLLOUT = new Key(
      'skillweb-company-new-enrollment-interface',
      false
    )

    static readonly ENROLLMENT_REQUIRES_BIOMETRY = new Key('skillweb-auth-require-biometry', true)

    static readonly SHOW_INSTITUTION_USER_PLATFORM_LINK = new Key(
      'skillcustomer-show-institution-user-platform-link',
      false
    )

    static readonly COMPANIES_ON_DEPENDENT_ROLLOUT = new Key(
      'skillcustomer-companies-on-dependent',
      false
    )

    static readonly COMPANIES_ON_USER_PROFILE = new Key(
      'skillcustomer-companies-on-user-profile',
      false
    )

    static readonly DEPENDENT_USER_PROFILE = new Key('skillcustomer-dependent-user-profile', false)

    static readonly RH_CREATE_COLLABORATOR_PROFILE = new Key(
      'skillcustomer-rh-create-collaborator-profile',
      false
    )

    static readonly RH_EDIT_COLLABORATOR_PROFILE = new Key(
      'skillcustomer-rh-edit-collaborator-profile',
      false
    )

    static readonly RH_EDIT_DEPENDENT_PROFILE = new Key(
      'skillcustomer-rh-edit-dependent-profile',
      false
    )

    static readonly DASHBOARDS_IMPACT_LICENSES_AND_RANKING = new Key(
      'skillcustomer-dashboards-impact-licenses-and-ranking',
      false
    )

    static readonly DISPLAY_CATEGORIES_HIGH_SCHOOL_AND_TECHNICAL = new Key(
      'skillweb-display-categories-high-school-and-technical',
      false
    )

    static readonly DISPLAY_COMPANY_CONTENT_RECOMMENDATIONS = new Key(
      'skillcustomer-display-company-content-recommendations',
      false
    )

    static readonly DISPLAY_AVAILABLE_CATEGORIES = new Key(
      'skillcustomer-display-available-categories',
      false
    )

    static readonly COMPANIES_ON_USER_SETTINGS = new Key(
      'skillcustomer-companies-on-user-security-privacy',
      false
    )

    static readonly COMPANIES_ON_USER_INTEREST_AREAS_SKILLS = new Key(
      'skillcustomer-companies-on-user-interest-areas-skills',
      false
    )

    static readonly NEW_ENROLLMENT_COMPLETION = new Key('skillweb-new-enrollment-completion', false)

    static readonly JOB_DEPARTMENT_REQUIRED = new Key(
      'skillcustomer-job-department-required',
      false
    )

    static readonly COMPANIES_WITH_CHATBOT = new Key('skillcustomer-companies-with-chatbot', false)

    static readonly COMPANIES_WITH_CUSTOM_HELP_CENTER = new Key(
      'skillcustomer-companies-custom-help-center',
      false
    )

    static readonly CATEGORIES_AGREEMENT_TERMS = new Key(
      'skillcustomer-categories-agreement-terms',
      false
    )

    static readonly COMPANIES_WITH_PROFILE_ASSESSMENT = new Key(
      'skillcustomer-companies-with-profile-assessment',
      false
    )

    static readonly LOAD_PROFILE_ASSESSMENT_ON_START = new Key(
      'skillcustomer-load-profile-assessment-on-start',
      true
    )

    static readonly INSTITUTIONS_WITH_LINK_HIDDEN = new Key(
      'skillcustomer-institutions-with-link-hidden',
      false
    )

    static readonly I18N = new Key('skillweb-i18n', false)

    static readonly SEARCH_LANGUAGES_FILTER = new Key(
      'skillcustomer-search-languages-filter',
      false
    )

    static readonly SEARCH_SUBSCRIPTION_FILTER = new Key(
      'skillcustomer-search-subscription-filter',
      false
    )

    static readonly USER_SPACE_ENABLED = new Key('skillcustomer-user-space-enabled', false)

    static readonly UPGRADE_REQUEST = new Key('skillcustomer-upgrade-request', false)

    static readonly DEPENDENT_REQUEST = new Key('skillcustomer-dependent-request', false)

    static readonly CUSTOM_SUPPORT_LINK = new Key('skillcustomer-custom-support-link', false)

    static readonly SUPPORT_ORIGIN_MULTIPLE_TABS = new Key(
      'skillcustomer-support-origin-multiple-tabs',
      false
    )

    static readonly DISABLED_PREFERRED_CONTENT_TYPE_BY_COMPANY_ID = new Key(
      'skillcustomer-disabled-preferred-content-type-by-company-id',
      {}
    )

    static readonly USER_BASED_CF_RECOMMENDATIONS = new Key(
      'skillcustomer-user-based-cf-recommendations',
      false
    )

    static readonly USER_GREETING = new Key('skillcustomer-user-greeting', false)

    static readonly FIRST_ACCESS = new Key('skillcustomer-first-access', false)

    static readonly INVISIBLE_RECAPTCHA = new Key('skillcustomer-invisible-recaptcha', false)

    static readonly USER_ENROLLMENTS_PAGE = new Key('skillcustomer-user-enrollments-page', false)

    static readonly DEFAULT_TRUE = new Key('default_true', true)

    static readonly SHOW_FULL_CATALOG = new Key('skillweb-show-full-catalog', false)

    static readonly ENABLED_UPGRADE_V4 = new Key('skillcustomer-upgrade-v4', false)

    static readonly ENABLE_CONTACT_VALIDATION = new Key('skillcustomer-contact-validation', false)

    static readonly PARTNER_REFUSED_MESSAGE = new Key(
      'skillcustomer-partner-refused-message',
      false
    )

    static readonly PARTNER_CANCEL_NOT_ALLOWED = new Key(
      'skillcustomer-partner-cancel-not-allowed',
      false
    )

    static readonly NAV_PDV_WITH_PRODUCT_ID = new Key(
      'skillcustomer-nav-pdv-with-product-id',
      false
    )

    static readonly ENABLE_FAVORITE_COURSES = new Key(
      'skillcustomer-enable-favorite-courses',
      false
    )

    static readonly ENGAGE_DATA = new Key('skillcustomer-company-dashboards-engage', {})

    static readonly SINGLE_SIGN_ON_OPTIONS = new Key('skillcustomer-single-sign-on-options', false)

    static readonly ENABLED_NEW_SOCIAL_NAME_FIELD = new Key('skillcustomer-social-name', false)

    static readonly PRODUCT_FIELD_MIGRATION = new Key('skillweb-product-field-migration', false)

    static readonly ENABLE_CYCLES = new Key('skillcustomer-enable-cycles', false)

    static readonly ENABLE_CYCLES_ENROLLMENT_STEP2 = new Key(
      'skillcustomer-enable-cycles-enrollment-step2',
      false
    )

    private constructor(
      public readonly name: string,
      public readonly defaultValue: unknown
    ) {
      Key.allValues[this.name] = this
    }

    public getValue(ldClient: LDClient | undefined) {
      return FeatureFlag.Helper.getFlagValue(ldClient, this)
    }

    public isEnabled(ldClient: LDClient | undefined): boolean {
      return FeatureFlag.Helper.getFlagValue(ldClient, this) as boolean
    }

    public checkEnabled(): boolean {
      return FeatureFlag.Helper.flagsMap?.[this.name] ?? this.defaultValue
    }

    static getAllKeys(): Key[] {
      return Object.values(Key.allValues)
    }

    public includesValue(value: string | number, ldClient: LDClient | undefined) {
      const listValue = this.getValue(ldClient) ?? []
      if (!Array.isArray(listValue)) {
        console.error(`Feature flag '${this.name}' value is not an array:`, listValue)
        return false
      }
      return listValue.includes(value)
    }
  }

  export class Helper {
    private static readonly USER_CONTEXT_KIND = 'user'
    static #flagsMap: LDFlagSet | undefined
    static #ld: LDClient | undefined = undefined

    static async identifyClient(ldClient: LDClient | undefined, accountProfile: Account.Model) {
      if (ldClient) {
        this.#ld = ldClient
        this.#flagsMap = await ldClient
          .identify({
            kind: this.USER_CONTEXT_KIND,
            key: accountProfile.id,
            accountType: accountProfile.accountType?.name,
            onboardingCompleted: accountProfile.onboardingCompleted,
            passPlanId: accountProfile.passPlan?.id,
            language: accountProfile.preferenceLanguage,
            companyId: accountProfile.company.id,
            companyUniqueId: accountProfile.company.uniqueId,
            companyName: accountProfile.company.name
          })
          .catch(e => {
            console.error('Error identifying LaunchDarkly client:', e)
            const defaultFlags: LDFlagSet = {}
            Key.getAllKeys().forEach(key => {
              defaultFlags[key.name] = key.defaultValue
            })
            return defaultFlags
          })

        ldClient.on('error', e => console.log('Feature Flag Initialization error:', e))
      }
    }

    static get flagsMap() {
      return Object.freeze(this.#flagsMap)
    }

    static getFlagValue(ldClient: LDClient | undefined, key: FeatureFlag.Key) {
      if (!ldClient) return key.defaultValue
      return ldClient.variation(key.name, key.defaultValue)
    }

    static getFlagVariationDetail(key: FeatureFlag.Key): LDEvaluationDetail {
      if (!this.#ld)
        return {
          value: key.defaultValue
        }
      return this.#ld.variationDetail(key.name, key.defaultValue)
    }

    static getExperiments() {
      if (!this.#ld) return {}

      return {}
    }
  }
}
